import {
	XIcon,
	WebsiteIcon,
	ShareDotsIcon,
	CaretLeft,
	ArrowRightIcon,
} from '../icons'
import Counter from '../badgesAndTags/counter'
import {
	TypographyCaps,
	TypographyDisplay,
	TypographyHeading,
	TypographyLabel,
	TypographyParagraph,
} from '../typography'
import { ContentTile, NftCard } from '../content'
import { SearchInput, Filter } from '../inputs'
import { useState } from 'react'
import { AttestationType, User } from '#types/loreTypes.tsx'
import { eventData, nft } from '#types/loreTypes.tsx'
import ProofOfExhibition from '../common/proofOfExhibition.tsx'
import {
	truncateStringInMiddle,
	chainIDMapping,
	getNFTProfileURL,
	getShareAttestationToXURL,
	getUserProfileURL,
} from '#app/utils/misc.tsx'
import InternalLink from '../common/internalLink.tsx'
import Button from '../buttons/button.tsx'
import learnmorerollups from '#images/learnmorerollups.webp'
import { Link } from '@remix-run/react'

const formatDate = (date: Date) => {
	try {
		const options = {
			year: 'numeric',
			month: 'short',
			day: '2-digit',
		}
		//@ts-ignore
		return date.toLocaleDateString('en-US', options)
	} catch (e) {
		return 'Unknown'
	}
}

export default function EventMetadataProfile({
	event,
	user,
	nftData,
	cid,
	metadataEntry,
	nftAttestations,
	atomicLoreUrl,
}: {
	event: eventData
	user: User
	nftData: nft[]
	cid: string
	metadataEntry: AttestationType
	nftAttestations: AttestationType[]
	atomicLoreUrl: string
}) {
	return (
		<div className="mx-auto mb-16 flex h-full w-full max-w-[1440px] flex-1 flex-col">
			<div className="flex flex-row items-center justify-between px-8 lg:px-24 xl:px-24">
				<InternalLink to={'/'} prefetch="none">
					<Button variant="tertiary">
						<div className="flex items-center">
							<CaretLeft />
							Home
						</div>
					</Button>
				</InternalLink>
				<NavigationHeader
					eventLink={event.eventLink}
					metadataEntry={metadataEntry}
					atomicLoreUrl={atomicLoreUrl}
				/>
			</div>
			<div className="mt-4 flex flex-col items-center justify-between gap-y-4 lg:px-8 xl:px-16">
				<TypographyCaps
					size="medium"
					additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
				>
					Event - {formatDate(new Date(event.dateStart))}
				</TypographyCaps>
				<TypographyDisplay
					size="large"
					additionalClasses="mx-40 text-center text-textPrimeryLight dark:text-textPrimaryDark"
				>
					{event.eventTitle}
				</TypographyDisplay>
				{event.eventImageUrl && (
					<img
						src={event.eventImageUrl}
						alt={event.eventTitle}
						className="mt-16 h-auto max-h-[390px] w-full object-contain sm:max-h-[720px]"
					/>
				)}
			</div>
			<div className="mt-28 flex flex-col gap-x-24 px-8 md:flex-row lg:px-20 xl:px-24">
				<div className="flex w-full flex-col md:w-1/2">
					{user.username && user.avatar && (
						<InternalLink to={getUserProfileURL(user.username)}>
							<ContentTile
								title={user.username}
								description={truncateStringInMiddle(metadataEntry.address)}
								image={user.avatar}
								isVerified={true}
								showCaret={false}
								showBorder={false}
								variant="user"
							/>
						</InternalLink>
					)}
					<div className=" flex flex-row justify-between border-t border-uiPrimaryLight py-6 dark:border-uiSecondaryDark">
						<TypographyLabel
							size="small"
							additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
						>
							ARTWORKS
						</TypographyLabel>
						<TypographyLabel
							size="small"
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
						>
							{nftData.length}
						</TypographyLabel>
					</div>
					<div className="flex flex-row justify-between border-t border-dotted border-textSecondaryLight py-6 dark:border-uiSecondaryDark">
						<TypographyLabel
							size="small"
							additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
						>
							ARTISTS
						</TypographyLabel>
						<TypographyLabel
							size="small"
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
						>
							{
								new Set(
									nftData
										.map(nft => nft.artist?.name)
										.filter(name => name !== undefined),
								).size
							}
						</TypographyLabel>
					</div>
					<div className="flex flex-row justify-between border-t border-dotted border-textSecondaryLight py-6 dark:border-uiSecondaryDark">
						<TypographyLabel
							size="small"
							additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
						>
							CHAINS
						</TypographyLabel>
						<TypographyLabel
							size="small"
							additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
						>
							{Array.from(
								new Set(
									nftData
										.map(nft => nft.chain)
										.filter(name => name !== undefined),
								),
							)
								.join(', ')
								.toUpperCase()}
						</TypographyLabel>
					</div>
				</div>
				<div className="mt-8 w-full md:mt-0 md:w-1/2">
					<TypographyParagraph
						size="medium"
						additionalClasses="text-textSecondaryLight dark:text-textSecondaryDark"
					>
						{event.eventDescription}
					</TypographyParagraph>
				</div>
			</div>
			<div className="flex flex-row px-8 lg:px-8 xl:px-12">
				<ProofOfExhibition
					exhibitions={[
						{
							...metadataEntry,
							properties: {
								...metadataEntry.properties,
								name: event.eventTitle,
								location: event.eventLocation,
								startDate: event.dateStart,
								endDate: event.dateEnd,
								receiverAddress: nftAttestations[0].properties.receiverAddress,
								ownerAddress: nftAttestations[0].properties.ownerAddress,
							},
						},
					]}
				/>
			</div>
			<div className="flex flex-row px-8 lg:px-20 xl:px-24">
				<NFTs nfts={nftData} />
			</div>
		</div>
	)
}

const NavigationHeader = ({
	eventLink,
	metadataEntry,
	atomicLoreUrl,
}: {
	eventLink: string
	metadataEntry: AttestationType
	atomicLoreUrl: string
}) => {
	return (
		<div className="mb-16 mt-16 flex px-0 md:flex-row md:items-center">
			<div className="ml-auto mr-0 flex items-center justify-between space-x-4 lg:mt-0">
				<a
					href={getShareAttestationToXURL(metadataEntry, atomicLoreUrl)}
					target="_blank"
				>
					<div className="flex h-12 w-12 items-center justify-center rounded-full border border-textPrimaryLight transition-colors hover:border-textPrimaryDark hover:bg-textPrimaryLight hover:text-textPrimaryDark dark:border-textPrimaryDark dark:text-textPrimaryDark dark:hover:border-textPrimaryLight dark:hover:bg-textPrimaryDark dark:hover:text-textPrimaryLight">
						<XIcon />
					</div>
				</a>
				<a href={eventLink} target="_blank" rel="noreferrer">
					<div className="flex h-12 w-12 items-center justify-center rounded-full border border-textPrimaryLight transition-colors hover:border-textPrimaryDark hover:bg-textPrimaryLight hover:text-textPrimaryDark dark:border-textPrimaryDark dark:text-textPrimaryDark dark:hover:border-textPrimaryLight dark:hover:bg-textPrimaryDark dark:hover:text-textPrimaryLight">
						<WebsiteIcon />
					</div>
				</a>
				<a
					href={getShareAttestationToXURL(metadataEntry, atomicLoreUrl)}
					target="_blank"
				>
					<div className="flex h-12 w-12 items-center justify-center rounded-full border border-textPrimaryLight transition-colors hover:border-textPrimaryDark hover:bg-textPrimaryLight hover:text-textPrimaryDark dark:border-textPrimaryDark dark:text-textPrimaryDark dark:hover:border-textPrimaryLight dark:hover:bg-textPrimaryDark dark:hover:text-textPrimaryLight">
						<ShareDotsIcon />
					</div>
				</a>
			</div>
		</div>
	)
}

const NFTs = ({ nfts }: { nfts: nft[] }) => {
	const [nameFilter, setNameFilter] = useState('')
	const [chainFilter, setChainFilter] = useState({
		title: 'All types',
		description: 'All types',
	})

	const options = [
		{ title: 'All types', description: 'All types' },
		{ title: 'Ethereum', description: 'Ethereum' },
		{ title: 'Solana', description: 'Solana' },
		{ title: 'Base', description: 'Base' },
		{ title: 'Tezos', description: 'Tezos' },
		{ title: 'Zora', description: 'Zora' },
		{ title: 'Polygon', description: 'Polygon' },
		{ title: 'Shape', description: 'Shape' },
	]

	const filterByName = (nfts: nft[]) => {
		return nameFilter
			? nfts.filter(nft =>
					nft.name.toLowerCase().includes(nameFilter.toLowerCase()),
				)
			: nfts
	}

	const filterByChain = (nfts: nft[]) => {
		if (!chainFilter) return nfts
		return chainFilter.title != 'All types'
			? nfts.filter(
					nft =>
						nft.chain &&
						nft.chain.toLowerCase().includes(chainFilter.title.toLowerCase()),
				)
			: nfts
	}

	return (
		<div className="my-24 flex w-full flex-col gap-y-4">
			<div className="mb-8 w-full justify-between sm:flex sm:flex-row">
				<div className="flex flex-1 items-center gap-x-4">
					<TypographyHeading
						size="medium"
						isBold={true}
						additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
					>
						NFTs in this event
					</TypographyHeading>
					<Counter size="small" children={nfts.length} />
				</div>
				<div className="mt-3 flex flex-1 items-center justify-start gap-x-4 sm:mt-0 sm:justify-end">
					<SearchInput
						name="Search"
						placeholder="Search"
						showShadow={false}
						value={nameFilter}
						isInactive={false}
						setValue={newVal => {
							setNameFilter(newVal)
						}}
						containerWidth="w-80 hidden lg:!block"
					/>
					<Filter
						type="Chain"
						options={options}
						onChange={newVal => setChainFilter(newVal)}
					></Filter>
				</div>
			</div>
			<div className="-mx-4 flex grid grid-cols-2 flex-wrap sm:!grid-cols-3 lg:!grid-cols-4">
				{filterByName(filterByChain(nfts)).map((nft: nft, index) => (
					<InternalLink
						key={`${nft.id}-${index}`}
						to={getNFTProfileURL(nft)}
						prefetch="none"
					>
						<div key={nft.id} className="mb-8 w-full px-4">
							<div className="w-full">
								<NftCard
									name={nft.name}
									image={nft.previewImage ? nft.previewImage : ''}
									artistName={nft.artist ? nft.artist.name : 'Unknown artist'}
									chain={chainIDMapping[nft.chain]}
									contract={nft.contract.address}
									tokenId={nft.tokenId}
									cardAdditionalProps="h-full w-auto"
									containerAdditionalProps="relative w-full pt-[100%] bg-grey100"
									imageAdditionalProps="absolute top-0 left-0 w-full h-full object-cover"
								/>
							</div>
						</div>
					</InternalLink>
				))}
			</div>
			<div className="bg-white dark:bg-black">
				<div className="mx-auto mb-24 mt-32 flex max-w-[1440px] flex-col items-center justify-center">
					<div className="flex flex-col items-center gap-x-6 gap-y-6 rounded-md bg-uiSecondaryLight p-4 dark:bg-uiSecondaryDark sm:flex-row">
						<img
							src={learnmorerollups}
							alt="Learn More"
							className="w-full rounded-md object-cover sm:h-24 sm:w-24"
						/>
						<div className="flex flex-col">
							<TypographyParagraph
								size="large"
								isBold={true}
								additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
							>
								Discover the Power of Roll-up Attestations
							</TypographyParagraph>
							<TypographyParagraph
								size="medium"
								additionalClasses="text-textPrimaryLight dark:text-textPrimaryDark"
							>
								Are you an artist or curator with thousands of exhibition
								records that need to be added onchain? Our new Roll-Up Feature
								allows you to securely sign bulk imports from spreadsheets or
								databases.
							</TypographyParagraph>
						</div>
						<Link
							to="https://www.atomicform.com/rollup"
							target="_blank"
							className="w-full sm:w-fit"
						>
							<Button
								variant="secondary"
								size="medium"
								rightIcon={<ArrowRightIcon />}
								additionalClasses="whitespace-nowrap w-full"
							>
								Learn More
							</Button>
						</Link>
					</div>
				</div>
			</div>
		</div>
	)
}
